// Edit comment form component

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateComment } from '@/hooks/useComments'
import { Loader2, Save, X } from 'lucide-react'
import { toast } from 'sonner'

interface EditCommentFormProps {
  author: string
  permlink: string
  parentAuthor: string
  parentPermlink: string
  rootAuthor: string
  rootPermlink: string
  initialBody: string
  onSuccess?: () => void
  onCancel: () => void
}

export function EditCommentForm({
  permlink,
  parentAuthor,
  parentPermlink,
  rootAuthor,
  rootPermlink,
  initialBody,
  onSuccess,
  onCancel,
}: EditCommentFormProps) {
  const [body, setBody] = useState(initialBody)

  const updateComment = useUpdateComment()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedBody = body.trim()

    if (!trimmedBody) {
      toast.error('댓글 내용을 입력해주세요')
      return
    }

    if (trimmedBody.length > 65535) {
      toast.error('댓글이 너무 깁니다 (최대 65535자)')
      return
    }

    if (trimmedBody === initialBody) {
      toast.info('변경된 내용이 없습니다')
      onCancel()
      return
    }

    try {
      await updateComment.mutateAsync({
        permlink,
        parentAuthor,
        parentPermlink,
        body: trimmedBody,
        rootAuthor,
        rootPermlink,
      })
      toast.success('댓글이 수정되었습니다')
      onSuccess?.()
    } catch (error) {
      toast.error('댓글 수정 실패', {
        description: error instanceof Error ? error.message : '알 수 없는 오류',
      })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Escape to cancel
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
    // Cmd/Ctrl + Enter to save
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit(e as unknown as React.FormEvent)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 space-y-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="댓글 내용을 수정하세요..."
        autoFocus
        disabled={updateComment.isPending}
        className="min-h-[80px] text-sm"
        rows={3}
        onKeyDown={handleKeyDown}
        aria-label="댓글 수정"
      />

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {body.length > 0 && `${body.length.toLocaleString()}자`}
          <span className="ml-2 hidden sm:inline">
            Esc로 취소, ⌘+Enter로 저장
          </span>
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={updateComment.isPending}
          >
            <X className="h-4 w-4 mr-1" />
            취소
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={!body.trim() || updateComment.isPending}
          >
            {updateComment.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                저장 중...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                저장
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
